import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchLoading } from './useSearchLoading';

type StringValue = { value: string; state: string };
type RangeValue = {
  start: number | string;
  end: number | string;
  state: string;
};
type SelectableValue = StringValue | RangeValue;

function getFacetKey(v: SelectableValue): string {
  if ('value' in v) return v.value;
  return `${v.start}-${v.end}`;
}

/**
 * Adds optimistic UI to Coveo facet checkbox selections.
 *
 * Immediately reflects a toggle in the UI without waiting for Coveo's
 * search response. Clears the optimistic layer when the response arrives,
 * and rolls back both the UI and the Coveo engine state (plus the URL hash,
 * which follows automatically via the URL manager) if the search fails.
 *
 * Usage:
 * ```tsx
 * const { optimisticValues, selectedCount, findStableValue, applyOptimistic } =
 *   useOptimisticFacetSelection(stableValues);
 *
 * // In click handler, resolve the stable value then apply + dispatch:
 * const stableValue = findStableValue(optimisticFacetValue);
 * if (!stableValue) return;
 * applyOptimistic([stableValue], () => controller.toggleSelect(stableValue));
 * controller.toggleSelect(stableValue);
 * ```
 *
 * The `rollback` function passed to `applyOptimistic` must re-dispatch the
 * same toggle operations so Coveo's engine state (and therefore the URL hash)
 * revert to their pre-click values on error.
 */
export function useOptimisticFacetSelection<T extends SelectableValue>(
  stableValues: T[]
): {
  optimisticValues: T[];
  selectedCount: number;
  findStableValue: (optimistic: T) => T | undefined;
  applyOptimistic: (toToggle: T[], rollback: () => void) => void;
} {
  const { isLoading, hasError } = useSearchLoading();

  const [pendingState, setPendingState] = useState<Map<string, string>>(
    new Map()
  );
  const rollbackRef = useRef<(() => void) | null>(null);

  // Clear optimistic layer once Coveo confirms the response (success path).
  useEffect(() => {
    if (!isLoading && !hasError && rollbackRef.current) {
      setPendingState(new Map());
      rollbackRef.current = null;
    }
  }, [isLoading, hasError]);

  // On error: undo Coveo engine state via the stored rollback, then clear the
  // optimistic layer. The URL hash reverts automatically because the URL
  // manager subscribes to engine state changes.
  useEffect(() => {
    if (hasError && rollbackRef.current) {
      rollbackRef.current();
      setPendingState(new Map());
      rollbackRef.current = null;
    }
  }, [hasError]);

  // Call immediately before dispatching a toggle to Coveo.
  // `toToggle` — the values being toggled (used to compute the new visual state).
  // `rollback` — re-dispatches the same operations to undo them on error.
  // Multiple calls compose: if the user clicks again before a response arrives,
  // both rollbacks are preserved so an error undoes all in-flight operations.
  const applyOptimistic = useCallback((toToggle: T[], rollback: () => void) => {
    const prevRollback = rollbackRef.current;
    rollbackRef.current = prevRollback
      ? () => {
          prevRollback();
          rollback();
        }
      : rollback;
    setPendingState((current) => {
      const next = new Map(current);
      for (const v of toToggle) {
        const key = getFacetKey(v);
        const effective = next.get(key) ?? v.state;
        next.set(key, effective === 'selected' ? 'idle' : 'selected');
      }
      return next;
    });
  }, []);

  const optimisticValues = useMemo(() => {
    if (pendingState.size === 0) return stableValues;
    return stableValues.map((v) => {
      const override = pendingState.get(getFacetKey(v));
      return override !== undefined ? { ...v, state: override } : v;
    });
  }, [stableValues, pendingState]);

  const selectedCount = useMemo(
    () => optimisticValues.filter((v) => v.state === 'selected').length,
    [optimisticValues]
  );

  // Look up the real (non-optimistic) value by key. Consumers need this because
  // controller.toggleSelect must receive the original Coveo value, not the
  // optimistic copy whose `state` may differ.
  const findStableValue = useCallback(
    (optimistic: T): T | undefined => {
      const key = getFacetKey(optimistic);
      return stableValues.find((v) => getFacetKey(v) === key);
    },
    [stableValues]
  );

  return { optimisticValues, selectedCount, findStableValue, applyOptimistic };
}
